/**
 * Dispute Wizard resume helpers — locate unfinished rounds and rebuild navigation state.
 */

import { isDataBreachLetter } from './disputeEnvelopeAttachments';
import {
  ACTIVE_PACKAGE_STORAGE_KEY,
  ACTIVE_ROUND_STORAGE_KEY,
  loadFinalPackageSnapshot,
} from './finalPackageRecovery';

export const RESUMABLE_ROUND_STATUSES = [
  'draft',
  'letters_generated',
  'pending_attachments',
  'ready_for_preview',
  'ready_for_delivery',
  'pending_send',
  'delivery_setup_incomplete',
  'generated',
  'building',
  'processing',
  'letters_ready',
  'delivery_pending',
];

const WIZARD_STEP_ROUTES = {
  select_items: '/dispute-wizard/factual/select-items',
  preview: '/dispute-wizard/factual/preview',
  supporting_documents: '/dispute-wizard/final-package',
  envelope_setup: '/dispute-wizard/step7-envelope',
  send: '/dispute-wizard/step8-send',
};

function withFinalPackageQuery(path, { packageId, roundId }) {
  if (!String(path).includes('final-package') || (!packageId && !roundId)) {
    return path;
  }
  const params = new URLSearchParams();
  if (packageId) params.set('package_id', String(packageId));
  if (roundId) params.set('round_id', String(roundId));
  const qs = params.toString();
  return `/dispute-wizard/final-package${qs ? `?${qs}` : ''}`;
}

const BUREAU_ADDRESSES = {
  equifax: {
    name: 'Equifax Information Services LLC',
    address_line_1: 'P.O. Box 740256',
    city: 'Atlanta',
    state: 'GA',
    zip: '30374',
  },
  experian: {
    name: 'Experian',
    address_line_1: 'P.O. Box 4500',
    city: 'Allen',
    state: 'TX',
    zip: '75013',
  },
  transunion: {
    name: 'TransUnion LLC',
    address_line_1: 'P.O. Box 2000',
    city: 'Chester',
    state: 'PA',
    zip: '19016',
  },
};

export function normalizeLettersFromRoundDetail(detail) {
  const generated = detail?.generated_letters || [];
  const packages = detail?.packages || [];
  const round = detail?.round || {};

  if (generated.length) {
    return generated.map((letter, idx) => ({
      ...letter,
      id: letter.letter_id || letter.id || `letter_${idx}`,
      letter_id: letter.letter_id || letter.id || `letter_${idx}`,
      bureau: letter.bureau || letter.target_bureau,
      html: letter.letter_html || letter.html || letter.preview_html || '',
      letter_html: letter.letter_html || letter.html || letter.preview_html || '',
      preview_html: letter.preview_html || letter.letter_html || letter.html,
      download_pdf_html: letter.download_pdf_html,
      postgrid_html: letter.postgrid_html,
      method_used: letter.package_method || letter.method_used || letter.method,
      package_method: letter.package_method || letter.method_used,
    }));
  }

  return packages
    .filter((pkg) => pkg?.letter_url)
    .map((pkg, idx) => ({
      id: pkg.id || pkg._id || `pkg_${idx}`,
      letter_id: pkg.id || pkg._id || `pkg_${idx}`,
      bureau: pkg.target_bureau,
      html: pkg.letter_url,
      letter_html: pkg.letter_url,
      method_used: pkg.letter_method || pkg.context?.method_used || 'factual_inaccuracy',
    }));
}

export function mergeAttachmentContext(detail) {
  const round = detail?.round || {};
  const packages = detail?.packages || [];
  const generated = detail?.generated_letters || [];
  const pkg = packages[0] || {};

  const packageIdentitySupport = {
    proof_of_address:
      pkg.package_identity_support?.proof_of_address ||
      pkg.identity_support?.proof_of_address ||
      round.package_identity_support?.proof_of_address ||
      null,
    government_id:
      pkg.package_identity_support?.government_id ||
      pkg.identity_support?.government_id ||
      round.package_identity_support?.government_id ||
      null,
    ssn_card:
      pkg.package_identity_support?.ssn_card ||
      pkg.identity_support?.ssn_card ||
      round.package_identity_support?.ssn_card ||
      null,
  };

  return {
    packageIdentitySupport,
    packageSharedAttachments: [
      ...(Array.isArray(pkg.package_shared_attachments) ? pkg.package_shared_attachments : []),
      ...(Array.isArray(round.package_shared_attachments) ? round.package_shared_attachments : []),
    ],
    perLetterAttachments: {
      ...(pkg.per_letter_attachments || {}),
      ...(round.per_letter_attachments || {}),
    },
    dataBreachProof: pkg.data_breach_proof || round.data_breach_proof || generated.find(isDataBreachLetter)?.data_breach_proof || null,
    ssnApprovals: { ...(pkg.ssn_approvals || {}), ...(round.ssn_approvals || {}) },
    senderAddresses: round.sender_addresses || pkg.sender_addresses || {},
  };
}

function buildEnvelopesFromLetters(letters, packageId) {
  return letters.map((letter, idx) => {
    const letterId =
      letter.letter_id ||
      letter.id ||
      letter._id ||
      `letter_${String(letter.bureau || 'unknown').toLowerCase()}_${idx}`;
    return {
      envelope_id: letterId,
      letter_id: letterId,
      package_id: letter.package_id || packageId,
      bureau: letter.bureau,
      method: letter.method || letter.package_method || letter.package_subtype || 'factual',
      envelope_type: isDataBreachLetter(letter) ? 'data_breach' : 'regular_dispute',
      recipient_address: BUREAU_ADDRESSES[String(letter.bureau || '').toLowerCase()] || null,
      letters: [letter],
      page_count: (letter.page_count || 1) + 2,
    };
  });
}

/** Rebuild Step 5 letter package + attachment context from a dispute-engine round detail payload. */
export function buildLetterPackageFromRoundDetail(detail, { packageId: preferredPackageId } = {}) {
  const round = detail?.round || {};
  const roundId = round.id || round._id;
  const letters = normalizeLettersFromRoundDetail(detail);
  const packageId =
    preferredPackageId ||
    round?.wizard_progress?.package_id ||
    detail?.packages?.[0]?.id ||
    detail?.packages?.[0]?._id ||
    null;
  const identityAnchor =
    round.consumer_identity ||
    round.consumer_info ||
    round.identity_anchor ||
    letters[0]?.consumer_info ||
    null;

  const letterPackage = {
    letters,
    round_id: roundId,
    package_id: packageId,
    consumer_info: identityAnchor,
    strategy: round.strategy_mode || round.family || 'factual',
  };

  return {
    letterPackage,
    stepState: {
      roundId,
      packageId,
      package_id: packageId,
      identityAnchor,
      consumerInfo: identityAnchor,
      letterPackage,
      ...mergeAttachmentContext(detail),
    },
    roundId,
    packageId,
    identityAnchor,
    family: round.family || round.strategy_mode || 'factual',
  };
}

export function buildResumeNavigation(detail, candidate) {
  const round = detail?.round || candidate?.round || {};
  const roundId = candidate?.round_id || round.id;
  const wizardStep = candidate?.wizard_step || candidate?.current_step || 'supporting_documents';
  const letters = normalizeLettersFromRoundDetail(detail);
  const packageId = candidate?.package_id || detail?.packages?.[0]?.id || detail?.packages?.[0]?._id || null;
  let path = candidate?.resume_path || WIZARD_STEP_ROUTES[wizardStep] || WIZARD_STEP_ROUTES.supporting_documents;
  path = withFinalPackageQuery(path, { packageId, roundId });
  const attachmentContext = mergeAttachmentContext(detail);
  const savedNavRaw = candidate?.wizard_navigation_state || round.wizard_progress?.wizard_navigation_state || {};
  const savedNav = { ...savedNavRaw };
  const identityAnchor =
    savedNav.identityAnchor ||
    savedNav.consumerInfo ||
    round.consumer_identity ||
    round.consumer_info ||
    round.identity_anchor ||
    letters[0]?.consumer_info ||
    null;

  const letterPackage = {
    letters,
    round_id: roundId,
    package_id: packageId,
    consumer_info: identityAnchor,
    strategy: round.strategy_mode || round.family || 'factual',
  };

  const effectiveLetterPackage = savedNav.letterPackage?.letters?.length
    ? savedNav.letterPackage
    : letterPackage;
  if (!savedNav.letterPackage?.letters?.length) {
    delete savedNav.letterPackage;
  }

  if (wizardStep === 'select_items') {
    return {
      path,
      state: {
        resume: true,
        roundId,
        roundData: round,
      },
      wizardStep,
    };
  }

  const baseState = {
    resume: true,
    roundId,
    letterPackage: effectiveLetterPackage,
    packageId: savedNav.packageId || savedNav.package_id || packageId,
    package_id: savedNav.package_id || savedNav.packageId || packageId,
    identityAnchor,
    consumerInfo: identityAnchor,
    ...attachmentContext,
    ...savedNav,
    letterPackage: effectiveLetterPackage,
    identityAnchor,
    consumerInfo: identityAnchor,
  };

  if (wizardStep === 'send' || wizardStep === 'envelope_setup') {
    baseState.envelopes = buildEnvelopesFromLetters(letters, packageId);
  }

  return { path, state: baseState, wizardStep };
}

export const WIZARD_SESSION_HINT_KEY = 'dispute_wizard_session_hint_v1';
const CLIENT_HINT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const STEP_LABELS = {
  select_items: 'Item Selection',
  ai_review: 'AI Review',
  letter_generation: 'Letter Generation',
  preview: 'Letter Preview',
  supporting_documents: 'Supporting Documents',
  envelope_setup: 'Envelope Setup',
  send: 'Send Mail',
};

export function packageSlotStorageKey(category) {
  return category === 'ssn_support' ? 'ssn_card' : category;
}

export function isMissingRoundError(error) {
  const message = String(error?.message || error || '');
  return (
    /not found/i.test(message)
    || /404/.test(message)
    || /Failed to get round/i.test(message)
  );
}

export function shouldRefreshResumeAfterStale404(alreadyAttempted) {
  return !alreadyAttempted;
}

export function clearStaleResumeLocalState(roundId) {
  const rid = String(roundId || '').trim();
  if (typeof localStorage === 'undefined') return;
  try {
    const active = localStorage.getItem(ACTIVE_ROUND_STORAGE_KEY);
    if (!rid || !active || String(active) === rid) {
      localStorage.removeItem(ACTIVE_ROUND_STORAGE_KEY);
    }
    const pkg = localStorage.getItem(ACTIVE_PACKAGE_STORAGE_KEY);
    if (pkg && rid && String(pkg).includes(rid)) {
      localStorage.removeItem(ACTIVE_PACKAGE_STORAGE_KEY);
    }
    const hintRaw = localStorage.getItem(WIZARD_SESSION_HINT_KEY);
    if (hintRaw) {
      try {
        const hint = JSON.parse(hintRaw);
        const hintRound = String(hint?.round_id || hint?.roundId || '');
        if (!rid || !hintRound || hintRound === rid) {
          localStorage.removeItem(WIZARD_SESSION_HINT_KEY);
        }
      } catch {
        localStorage.removeItem(WIZARD_SESSION_HINT_KEY);
      }
    }
  } catch {
    /* ignore */
  }
}

export function saveWizardSessionHint({
  roundId,
  packageId,
  currentStep,
  family,
  letterCount,
  wizardStatus,
} = {}) {
  if (!roundId || !currentStep) return;
  try {
    localStorage.setItem(
      WIZARD_SESSION_HINT_KEY,
      JSON.stringify({
        round_id: roundId,
        package_id: packageId || null,
        current_step: currentStep,
        wizard_step: currentStep,
        family: family || 'factual',
        letter_count: letterCount || 0,
        wizard_status: wizardStatus || null,
        updated_at: new Date().toISOString(),
      })
    );
  } catch (error) {
    console.warn('[wizard session hint] save failed', error);
  }
}

export function loadWizardSessionHint() {
  try {
    const raw = localStorage.getItem(WIZARD_SESSION_HINT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const updatedAt = data.updated_at || data.updatedAt;
    if (updatedAt && Date.now() - new Date(updatedAt).getTime() > CLIENT_HINT_TTL_MS) {
      localStorage.removeItem(WIZARD_SESSION_HINT_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/** Build a resume card candidate from browser-stored wizard hints when API returns none. */
export function buildClientResumeCandidate() {
  let hint = loadWizardSessionHint();
  const snapshot = loadFinalPackageSnapshot({});
  const roundId =
    hint?.round_id ||
    snapshot?.roundId ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_ROUND_STORAGE_KEY) : null);
  const packageId =
    hint?.package_id ||
    snapshot?.packageId ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_PACKAGE_STORAGE_KEY) : null);
  const stepState = snapshot?.stepState || {};
  const currentStep =
    hint?.current_step ||
    hint?.wizard_step ||
    stepState.currentStep ||
    (snapshot?.letterPackage?.letters?.length ? 'supporting_documents' : 'preview');

  if (!roundId && !packageId && !snapshot?.letterPackage?.letters?.length) {
    return null;
  }

  if (!hint && (roundId || packageId || snapshot)) {
    hint = {
      round_id: roundId,
      package_id: packageId,
      current_step: currentStep,
      wizard_step: currentStep,
      letter_count: snapshot?.letterPackage?.letters?.length || 0,
      family: snapshot?.family || 'factual',
      updated_at: snapshot?.timestamp
        ? new Date(snapshot.timestamp).toISOString()
        : new Date().toISOString(),
    };
  }

  if (!hint?.round_id && !hint?.package_id) return null;

  const step = hint.current_step || hint.wizard_step || 'select_items';
  if (step === 'completed') return null;

  const stepLabel = STEP_LABELS[step] || step.replace(/_/g, ' ');

  return {
    round_id: hint.round_id,
    package_id: hint.package_id,
    wizard_step: step,
    current_step: step,
    step_name: stepLabel,
    wizard_step_label: stepLabel,
    resume_path: WIZARD_STEP_ROUTES[step] || WIZARD_STEP_ROUTES.select_items,
    letter_count: hint.letter_count || 0,
    family: hint.family || 'factual',
    updated_at: hint.updated_at,
    is_resumable: true,
    source: 'client_storage',
  };
}

export function formatResumeRoundLabel(candidate) {
  if (candidate?.round_number) {
    return `Round ${candidate.round_number}`;
  }
  return 'Dispute Round';
}

export function formatResumeDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}
